import React, { useCallback, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import DownloadIcon from '@mui/icons-material/Download';
import LoadingButton from '@mui/lab/LoadingButton';
import Typography from '@mui/material/Typography';
import Alert from '@mui/material/Alert';
import Grid from '@mui/material/Grid';
import Paper from '@mui/material/Paper';
import Stack from '@mui/material/Stack';
import { GridColDef } from '@mui/x-data-grid-premium';

import { reportService } from '../../api';
import { Table } from '../../components/Table';
import { fetchStatuses } from '../../constants/fetchStatuses';
import { fetchCmaUsersReport } from '../../redux/modules/report/report.actions';
import { reportSelector } from '../../redux/modules/report/report.selectors';
import type { ReportApiDateRangeQuery } from '../../types/report';

import { ReportDateRangeFilter } from './ReportDateRangeFilter';
import { useReportCsvExport } from './useReportCsvExport';

const CMA_USERS_CSV_FILENAME = 'cma-users-report.csv';

export function CMAUsersReportPage() {
  const [dateRange, setDateRange] = useState<ReportApiDateRangeQuery | null>(null);

  const onDateRangeChange = useCallback((r: ReportApiDateRangeQuery | null) => {
    setDateRange(r);
  }, []);

  const columns = useRef<GridColDef[]>([
    { field: 'national_id_iqama', headerName: 'National ID', width: 200 },
    { field: 'age', headerName: 'Age', width: 120 },
    { field: 'gender', headerName: 'Gender', width: 150 },
    { field: 'nationality', headerName: 'Nationality', flex: 1, minWidth: 180 },
    { field: 'status', headerName: 'Status', width: 150 },
    { field: 'location', headerName: 'Location', flex: 1, minWidth: 180 },
  ]);

  const { cmaUsersItems, totalCmaUsersItems, fetchCmaUsersReportStatus, error } = useSelector(reportSelector);

  const { exportCsv, loading: exportCsvLoading } = useReportCsvExport(
    CMA_USERS_CSV_FILENAME,
    useCallback(() => reportService.exportCmaUsersReportCsv(), []),
  );

  return (
    <>
      {error && <Alert severity="error">{error.message}</Alert>}

      {cmaUsersItems && cmaUsersItems.length === 0 && fetchCmaUsersReportStatus === fetchStatuses.success && (
        <Alert severity="info">No CMA users found for the selected date range.</Alert>
      )}

      <Grid container flexDirection="column" component={Paper} sx={{ p: 3, mt: 0 }}>
        <ReportDateRangeFilter onRangeChange={onDateRangeChange} />

        <Stack
          direction="row"
          alignItems="center"
          justifyContent="space-between"
          sx={{ mb: 2 }}
          flexWrap="wrap"
          gap={1}
        >
          <Typography variant="h6">CMA Users</Typography>

          <LoadingButton
            type="button"
            variant="outlined"
            color="primary"
            endIcon={<DownloadIcon />}
            loading={exportCsvLoading}
            loadingPosition="end"
            onClick={exportCsv}
          >
            Export all CSV
          </LoadingButton>
        </Stack>

        <Table
          wrapperHeight={700}
          fetchItems={fetchCmaUsersReport}
          fetchItemsCustomParams={dateRange ?? {}}
          rows={cmaUsersItems}
          columns={columns.current}
          getRowId={(row) => row.national_id_iqama}
          loading={fetchCmaUsersReportStatus === fetchStatuses.pending}
          rowCount={totalCmaUsersItems}
          initialSortModel={[{ field: 'national_id_iqama', sort: 'desc' }]}
        />
      </Grid>
    </>
  );
}
